/**
 * @file Core-service's customer record in the shape Django's customer retrieve returned. The account
 * screen PUTs a copy of each account back to Django, so `schedule_days` and `additional_data` must
 * survive the trip: a PUT without them resets them.
 */

// Django sends integer pkids, and readers compare them with `===`.
function toPkid(value) {
  return value == null ? null : Number(value)
}

// Django's invoice items carry no id.
function toInvoiceItem(item) {
  return { description: item.description, amount: item.amount }
}

// Django leaves out the mask that does not apply to the method; core sends it as null.
function toSourcePaymentMethod(method) {
  if (!method) return null
  const mapped = { nickname: method.nickname }
  if (method.maskedCardNumber != null) {
    mapped.masked_card_number = method.maskedCardNumber
  }
  if (method.maskedAccountNumber != null) {
    mapped.masked_account_number = method.maskedAccountNumber
  }

  return mapped
}

function toScheduledPaymentStatus(status) {
  if (!status) return null

  return {
    scheduled_date: status.scheduledDate,
    sources: (status.sources || []).map(source => ({
      amount: source.amount,
      payment_type: source.paymentType,
      payment_method: toSourcePaymentMethod(source.paymentMethod)
    }))
  }
}

function toCurrentInvoice(invoice) {
  if (!invoice) return null

  return {
    id: invoice.id,
    due_date: invoice.dueDate,
    amount_due: invoice.amountDue,
    items: (invoice.items || []).map(toInvoiceItem),
    scheduled_payment_status: toScheduledPaymentStatus(
      invoice.scheduledPaymentStatus
    )
  }
}

function toAccount(account) {
  return {
    id: account.id,
    pkid: toPkid(account.pkid),
    name: account.name,
    description: account.description,
    external_id: account.externalId,
    status: account.status,
    merchant_location: account.merchantLocationId,
    amount_due: account.amountDue,
    due_date: account.dueDate,
    waive_fees: account.waiveFees,
    in_person: account.inPerson,
    allow_partial_payment: account.allowPartialPayment,
    schedule: account.schedule,
    schedule_days: account.scheduleDays,
    tags: (account.tags || []).map(toPkid),
    account_manager_id: toPkid(account.accountManagerPkid),
    additional_data: account.additionalData,
    is_autopay: account.isAutopay,
    autopay_customer: account.autopayCustomerPkid,
    autopay_payment_method: account.autopayPaymentMethodId,
    autopay_enabled_by: account.autopayEnabledBy,
    autopay_payment_last_four: account.autopayPaymentLastFour,
    autopay_config: account.autopayConfig && {
      is_enabled: account.autopayConfig.isEnabled,
      is_custom: account.autopayConfig.isCustom
    },
    has_ituran: account.hasIturan,
    last_payment_amount: account.lastPaymentAmount,
    last_payment_date: account.lastPaymentDate,
    last_external_update: account.lastExternalUpdate,
    invoice_locked_until: account.invoiceLockedUntil ?? null,
    last_worked: account.lastWorked && {
      worked_at: account.lastWorked.workedAt,
      worked_by_name: account.lastWorked.workedByName
    },
    current_invoice: toCurrentInvoice(account.currentInvoice),
    skipped_invoice: account.skippedInvoice && {
      id: account.skippedInvoice.id,
      due_date: account.skippedInvoice.dueDate
    },
    promises: account.promises,
    scheduled_payment: account.scheduledPayment,
    promise_objects: (account.promiseObjects || []).map(promise => ({
      status: promise.status
    })),
    notes: (account.notes || []).map(note => ({ body: note.body }))
  }
}

/**
 * Maps `GET /core/v1/merchant/{pkid}/customers/{uuid}` to the keys Django's retrieve used, field by
 * field. `disabled_payment_methods` and `additional_data` pass through whole: their keys are data.
 * An account's `autopay_customer` holds core's pkid string, not Django's UUID: read it for truthiness
 * only.
 * @param {object} customer The core response body.
 * @returns {object} The customer with Django's key names and integer pkids.
 */
export function toDjangoCustomerShape(customer) {
  return {
    id: customer.id,
    pkid: toPkid(customer.pkid),
    external_id: customer.externalId,
    first_name: customer.firstName,
    last_name: customer.lastName,
    address: customer.address,
    address2: customer.address2,
    city: customer.city,
    state_province: customer.stateProvince,
    postal_code: customer.postalCode,
    sms_phone_number: customer.smsPhoneNumber,
    date_of_birth: customer.dateOfBirth,
    email: customer.email,
    ssn_last_four: customer.ssnLastFour,
    is_registered: customer.isRegistered,
    disabled_payment_methods: customer.disabledPaymentMethods,
    optins: (customer.optins || []).map(optin => ({
      id: optin.id,
      category: optin.category,
      media: optin.media,
      status: optin.status
    })),
    user: customer.user && {
      id: customer.user.id,
      pkid: toPkid(customer.user.pkid),
      first_name: customer.user.firstName,
      last_name: customer.user.lastName,
      email: customer.user.email
    },
    accounts: (customer.accounts || []).map(toAccount)
  }
}
